import { ReactNode, useRef, useEffect } from "react";
import {
    SvgIcon,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@mui/material";

import "./style.scss";
import { ReactComponent as PlaceholderIcon } from "../../assets/placeholder--loader.icon.svg";
import {
    SortableContext,
    horizontalListSortingStrategy,
    useSortable,
    arrayMove,
    sortableKeyboardCoordinates
} from "@dnd-kit/sortable";
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

const SortableTableCell = ({
    children,
    column,
    className = ""
}: {
    children: ReactNode;
    column: string;
    className?: string;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: column
    });
    const style = {
        transform: CSS.Translate.toString(transform),
        transition
    };
    return (
        <TableCell
            ref={setNodeRef}
            className={className}
            style={style}
            {...attributes}
            {...listeners}
        >
            {children}
        </TableCell>
    );
};

export const DynamicTable = ({
    list,
    columns,
    setColumns,
    annotation,
    scrollIntoView,
    ariaLabel
}: {
    list: any[];
    columns: string[];
    setColumns: (arg1: string[]) => void;
    annotation: any;
    scrollIntoView?: number;
    ariaLabel?: string;
}) => {
    const ref = useRef<HTMLTableRowElement>(null);

    const parseTitle = (key: string) =>
        (key.split(".").pop() ?? key)
            .split(/[a-z](?=[A-Z])/g)
            .join(" ")
            .toLowerCase()
            .firstLetterToUppercase();

    const getTitle = (key: string) =>
        annotation?.dimensions[key]?.shortTitle ??
        annotation?.measures[key]?.shortTitle ??
        parseTitle(key);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const handleDragEnd = ({ active, over }: { active: any; over: any }) => {
        if (!over || active.id == over.id) return;

        setColumns(
            arrayMove(
                columns,
                columns.findIndex(column => column == active.id),
                columns.findIndex(column => column == over.id)
            )
        );
    };

    useEffect(() => {
        if (!scrollIntoView || !ref.current) return;

        ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, [scrollIntoView]);

    return (
        <div className="dynamic-table-view">
            {list.length > 0 ? (
                <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                    <TableContainer>
                        <Table stickyHeader className="dynamic-table" aria-label={ariaLabel}>
                            <TableHead className="dynamic-table-header">
                                <TableRow className="dynamic-table-header-row ">
                                    <SortableContext
                                        items={columns}
                                        strategy={horizontalListSortingStrategy}
                                    >
                                        {columns.map(column => (
                                            <SortableTableCell
                                                key={column}
                                                column={column}
                                                className="dynamic-table-header-cell"
                                            >
                                                <span className="dynamic-table-header-cell__text">
                                                    {getTitle(column)}
                                                </span>
                                            </SortableTableCell>
                                        ))}
                                    </SortableContext>
                                </TableRow>
                            </TableHead>
                            <TableBody className="dynamic-table-body">
                                {list.map((item, index) => (
                                    <TableRow
                                        ref={scrollIntoView === index ? ref : null}
                                        key={crypto.randomUUID()}
                                        className={`dynamic-table-row${scrollIntoView === index ? " dynamic-table-row--selected" : ""}`}
                                    >
                                        {columns.map(column => (
                                            <TableCell key={column} className="dynamic-table-cell">
                                                <span className="dynamic-table-cell__text">
                                                    {item[column]}
                                                </span>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DndContext>
            ) : (
                <div className="dynamic-table-placeholder">
                    <div className="dynamic-table-placeholder-content">
                        <SvgIcon
                            className="dynamic-table-placeholder-content__icon"
                            component={PlaceholderIcon}
                            inheritViewBox
                        />
                        <span className="dynamic-table-placeholder-content__title">Ooops</span>
                        <span className="dynamic-table-placeholder-content__description">
                            There is no data to show for your request.
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};
